import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { MessageSquare, Users, Trash2, Reply } from "lucide-react";
import { api, errMsg, fmtDate, titleUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/hooks/useSeo";
import { Avatar, Spoiler, Empty, Loading, ErrorBox } from "@/components/Common";
import { LikeButton } from "@/components/Reviews";
import { ReportButton } from "@/components/ReportButton";

const KINDS = [["", "All"], ["discussion", "Discussions"], ["question", "Questions"], ["theory", "Theories"], ["recommendation", "Recommendations"]];
const inp = "w-full rounded-xl border border-white/10 bg-black/30 px-4 py-2.5 text-sm outline-none focus:border-purple-400";

function MoviePick({ value, onChange }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  useEffect(() => { if (q.length < 2) return setRes([]); const id = setTimeout(() => api.get("/search", { params: { q } }).then((r) => setRes(r.data.titles)), 250); return () => clearTimeout(id); }, [q]);
  if (value) return <p className="text-sm">Attached: <span className="text-amber-400">{value.title} ({value.year})</span> <button type="button" onClick={() => onChange(null)} className="ml-2 text-xs text-slate-500">remove</button></p>;
  return (
    <div className="relative">
      <input data-testid="post-movie-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Attach a movie (optional)" className={inp} />
      {res.length > 0 && <div className="absolute z-20 mt-1 w-full rounded-xl border border-white/10 bg-[#12121A] p-1">{res.map((t) => <button type="button" key={t.id} onClick={() => { onChange(t); setQ(""); }} className="block w-full rounded-lg px-3 py-2 text-left text-sm hover:bg-white/5">{t.title} ({t.year})</button>)}</div>}
    </div>
  );
}

function Composer({ onPosted }) {
  const [f, setF] = useState({ kind: "discussion", title: "", body: "", spoiler: false });
  const [movie, setMovie] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/community/posts", { ...f, title_id: movie?.id || null }); toast.success("Posted to the community"); setF({ ...f, title: "", body: "" }); setMovie(null); onPosted(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <form onSubmit={submit} className="space-y-3 rounded-2xl border border-white/5 bg-[#12121A] p-5" data-testid="post-composer">
      <div className="flex flex-wrap gap-2">{KINDS.slice(1).map(([k, l]) => <button type="button" key={k} onClick={() => setF({ ...f, kind: k })} className={`rounded-full border px-3 py-1 text-xs ${f.kind === k ? "border-purple-400 bg-purple-500/15 text-purple-200" : "border-white/10 text-slate-400"}`}>{l.replace(/s$/, "")}</button>)}</div>
      <input data-testid="post-title" required minLength={3} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Title" className={inp} />
      <textarea data-testid="post-body" required minLength={3} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} placeholder="Share your thoughts…" className={`${inp} min-h-[100px]`} />
      <MoviePick value={movie} onChange={setMovie} />
      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 text-sm text-slate-400"><input type="checkbox" data-testid="post-spoiler" checked={f.spoiler} onChange={(e) => setF({ ...f, spoiler: e.target.checked })} className="accent-amber-400" />Contains spoilers</label>
        <button data-testid="post-submit" className="rounded-full bg-purple-600 px-6 py-2 text-sm font-bold hover:bg-purple-500">Publish</button>
      </div>
    </form>
  );
}

export function PostCard({ p }) {
  return (
    <article data-testid={`post-${p.id}`} className="rounded-2xl border border-white/5 bg-[#12121A] p-5 hover:border-purple-400/20 transition-colors">
      <div className="flex items-center gap-2 text-xs text-slate-500">
        <Link to={`/u/${p.user.username}`} className="flex items-center gap-2 font-semibold text-slate-200 hover:text-amber-400"><Avatar user={p.user} size={26} />@{p.user.username}</Link>· {fmtDate(p.created_at)}
        <span className="ml-auto rounded-full bg-purple-500/15 px-2 py-0.5 text-[10px] font-bold uppercase text-purple-300">{p.kind}</span>
      </div>
      <Link to={`/community/post/${p.id}`}><h3 className="mt-3 text-lg font-semibold hover:text-amber-400">{p.title}</h3></Link>
      <Spoiler spoiler={p.spoiler} id={p.id}><p className="mt-1 line-clamp-3 text-sm text-slate-400">{p.body}</p></Spoiler>
      {p.movie && <Link to={titleUrl(p.movie)} className="mt-3 inline-flex items-center gap-2 rounded-lg bg-white/5 p-1.5 pr-3 text-xs hover:bg-white/10"><img src={p.movie.poster?.replace("/w500/", "/w92/")} alt="" className="h-8 w-6 rounded object-cover" />{p.movie.title} ({p.movie.year})</Link>}
      <div className="mt-3 flex items-center gap-4"><LikeButton type="post" item={p} /><Link to={`/community/post/${p.id}`} className="flex items-center gap-1 text-xs text-slate-500"><MessageSquare className="h-3.5 w-3.5" />{p.comments}</Link><ReportButton type="post" id={p.id} /></div>
    </article>
  );
}

export function Community() {
  const { user } = useAuth();
  const [kind, setKind] = useState("");
  const [sort, setSort] = useState("recent");
  const [items, setItems] = useState(null);
  const [page, setPage] = useState(1);
  const [more, setMore] = useState(false);
  useSeo({ title: "Community", description: "Movie discussions, theories, questions and recommendations from real members." });
  const load = useCallback(async (pg = 1) => {
    const { data } = await api.get("/community/feed", { params: { kind: kind || undefined, sort, page: pg } });
    setItems((prev) => (pg === 1 ? data.items : [...(prev || []), ...data.items])); setMore(data.has_more); setPage(pg);
  }, [kind, sort]);
  useEffect(() => { setItems(null); load(1).catch(() => setItems([])); }, [load]);
  return (
    <div className="mx-auto max-w-3xl px-4 py-10" data-testid="community-page">
      <p className="eyebrow">Community</p>
      <h1 className="font-display text-6xl">Talk Movies</h1>
      <p className="mt-1 text-sm text-slate-400">Theories, questions and recommendations from real members. Mark your spoilers.</p>
      <div className="mt-6">{user ? <Composer onPosted={() => load(1)} /> : <p className="rounded-2xl border border-white/5 bg-[#12121A] p-5 text-sm"><Link to="/login?next=/community" className="font-semibold text-amber-400">Sign in</Link> or <Link to="/register" className="font-semibold text-amber-400">create an account</Link> to post.</p>}</div>
      <div className="mt-8 flex flex-wrap items-center gap-2">
        {KINDS.map(([k, l]) => <button key={k} data-testid={`feed-kind-${k || "all"}`} onClick={() => setKind(k)} className={`rounded-full px-3 py-1.5 text-xs font-semibold ${kind === k ? "bg-amber-400 text-black" : "bg-white/5 text-slate-300"}`}>{l}</button>)}
        <select data-testid="feed-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="ml-auto rounded-full border border-white/10 bg-[#12121A] px-3 py-1.5 text-xs"><option value="recent">Recent</option><option value="popular">Popular</option></select>
      </div>
      <div className="mt-5 space-y-4">
        {!items ? <Loading /> : items.length === 0 ? <Empty icon={Users} title="No posts yet" text="The community is brand new. Start the very first conversation!" testid="feed-empty" /> : items.map((p) => <PostCard key={p.id} p={p} />)}
        {more && <button data-testid="feed-load-more" onClick={() => load(page + 1)} className="w-full rounded-full border border-white/10 py-3 text-sm hover:border-amber-400">Load more</button>}
      </div>
    </div>
  );
}

function threaded(all) {
  const out = [];
  const walk = (parent, depth) => all.filter((x) => (x.parent_id || null) === parent).forEach((c) => { out.push({ c, depth }); walk(c.id, depth + 1); });
  const ids = new Set(all.map((x) => x.id));
  walk(null, 0);
  all.filter((x) => x.parent_id && !ids.has(x.parent_id)).forEach((c) => out.push({ c, depth: 0 }));
  return out;
}

function CommentItem({ c, depth, onReply, onDelete, me }) {
  return (
    <div data-testid={`comment-${c.id}`} className="border-l border-white/10 pl-4" style={{ marginLeft: Math.min(depth, 4) * 20 }}>
      <div className="flex items-center gap-2 text-xs"><Avatar user={c.user} size={22} /><Link to={`/u/${c.user.username}`} className="font-semibold">@{c.user.username}</Link><span className="text-slate-500">{fmtDate(c.created_at)}</span></div>
      <Spoiler spoiler={c.spoiler} id={c.id}><p className="mt-1 whitespace-pre-line text-sm text-slate-300">{c.body}</p></Spoiler>
      <div className="mt-1 flex gap-4">
        <LikeButton type="comment" item={c} />
        {me && <button data-testid={`reply-${c.id}`} onClick={() => onReply(c)} className="flex items-center gap-1 text-xs text-slate-500 hover:text-white"><Reply className="h-3.5 w-3.5" />Reply</button>}
        <ReportButton type="comment" id={c.id} />
        {me?.id === c.user_id && <button onClick={() => onDelete(c.id)} className="text-xs text-slate-500 hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>}
      </div>
    </div>
  );
}

export function PostPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  const [body, setBody] = useState("");
  const [spoiler, setSpoiler] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const load = useCallback(() => api.get(`/community/posts/${id}`).then((r) => setD(r.data)).catch(() => setErr(true)), [id]);
  useEffect(() => { load(); }, [load]);
  useSeo({ title: d?.post.title || "Discussion", description: d?.post.spoiler ? "Spoiler discussion" : d?.post.body?.slice(0, 150) });
  if (err) return <ErrorBox text="This post doesn't exist or was removed." />;
  if (!d) return <Loading />;
  const send = async (e) => {
    e.preventDefault();
    try { await api.post(`/community/posts/${id}/comments`, { body, spoiler, parent_id: replyTo?.id || null }); setBody(""); setReplyTo(null); load(); }
    catch (e2) { toast.error(errMsg(e2)); }
  };
  const del = async (cid) => { await api.delete(`/community/comments/${cid}`); load(); };
  const delPost = async () => { if (!window.confirm("Delete this post?")) return; await api.delete(`/community/posts/${id}`); nav("/community"); };
  return (
    <div className="mx-auto max-w-3xl px-4 py-10" data-testid="post-page">
      <Link to="/community" className="text-xs text-slate-500 hover:text-amber-400">← Community</Link>
      <div className="mt-4"><PostCard p={{ ...d.post, body: d.post.body }} /></div>
      <div className="mt-2 whitespace-pre-line rounded-2xl bg-[#0F0F16] p-5 text-sm text-slate-300"><Spoiler spoiler={d.post.spoiler} id={`full-${d.post.id}`}>{d.post.body}</Spoiler></div>
      {user?.id === d.post.user_id && <button onClick={delPost} data-testid="delete-post" className="mt-2 text-xs text-red-400">Delete post</button>}
      <h2 className="mt-10 font-display text-3xl">Comments ({d.comments.length})</h2>
      {user ? (
        <form onSubmit={send} className="mt-4 space-y-2" data-testid="comment-form">
          {replyTo && <p className="text-xs text-purple-300">Replying to @{replyTo.user.username} <button type="button" onClick={() => setReplyTo(null)} className="text-slate-500">cancel</button></p>}
          <textarea data-testid="comment-body" required value={body} onChange={(e) => setBody(e.target.value)} placeholder="Add a comment…" className={`${inp} min-h-[80px]`} />
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-xs text-slate-400"><input type="checkbox" checked={spoiler} onChange={(e) => setSpoiler(e.target.checked)} className="accent-amber-400" />Spoiler</label>
            <button data-testid="comment-submit" className="rounded-full bg-amber-400 px-5 py-2 text-sm font-bold text-black">Comment</button>
          </div>
        </form>
      ) : <p className="mt-3 text-sm"><Link to={`/login?next=/community/post/${id}`} className="text-amber-400">Sign in</Link> to comment.</p>}
      <div className="mt-6 space-y-5">{threaded(d.comments).map(({ c, depth }) => <CommentItem key={c.id} c={c} depth={depth} onReply={setReplyTo} onDelete={del} me={user} />)}</div>
    </div>
  );
}
